// ds-color-ignore-file: system font stack in dev/internal file
"use client"

import { useEffect, useRef, useState } from "react"
import { EditModeHost } from "./edit-mode-host"
import { ReviewWorklistBody } from "../review/review-worklist"
import { CockpitCart } from "./cockpit-cart"
import type { CartItem } from "./edit-cart"
import { useIsMobile } from "./use-is-mobile"
import { MobileCockpit } from "./mobile-cockpit"

type Tab = "edit" | "review"

const COCKPIT_TAB_KEY = "ahead-cockpit-tab"

/**
 * The cockpit lives in the persistent PageShell, but a jump / auto-advance that
 * changes the `[locale]` route segment remounts that subtree, so this runs again
 * on every cross-locale navigation. Land on Review when the URL asks for it —
 * `?review-cockpit` (a reviewer opening the cockpit) OR `?review=<key>` (the
 * param a jump/auto-advance carries) — otherwise restore the last tab from
 * session storage so stepping the worklist never silently drops back to Edit.
 */
function initialTab(): Tab {
  if (typeof window === "undefined") return "edit"
  const params = new URLSearchParams(window.location.search)
  if (params.has("review-cockpit") || params.has("review")) return "review"
  return sessionStorage.getItem(COCKPIT_TAB_KEY) === "review"
    ? "review"
    : "edit"
}

/** Glossary page URL for the country/locale currently in the address bar. */
function glossaryHref(): string {
  if (typeof window === "undefined") return "/ch/en/dev/glossary"
  const segs = window.location.pathname.split("/").filter(Boolean)
  return `/${segs[0] || "ch"}/${segs[1] || "en"}/dev/glossary`
}

/**
 * The cockpit head's overflow menu (⋯). Folds the secondary toolbar actions —
 * "Your scope", Glossary, and Done — behind one button so the head stays
 * uncramped beside the Edit|Review tabs (the row was overflowing once Glossary
 * + Done landed). Scope + Glossary are review-only; Done always shows. The menu
 * closes on item activation, outside-click, or Escape.
 */
function CockpitHeadMenu({
  tab,
  scopeOpen,
  onToggleScope,
  onExit,
}: {
  tab: Tab
  scopeOpen: boolean
  onToggleScope: () => void
  onExit: () => void
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  return (
    <div className="ahead-cockpit-menu" ref={wrapRef}>
      <button
        type="button"
        className="ahead-cockpit-menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        title="More actions"
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {open ? (
        <div className="ahead-cockpit-menu-list" role="menu">
          {tab === "review" ? (
            <button
              type="button"
              role="menuitem"
              className="ahead-cockpit-menu-item"
              aria-pressed={scopeOpen}
              onClick={() => {
                onToggleScope()
                setOpen(false)
              }}
            >
              <span>Your scope</span>
              <span aria-hidden="true">{scopeOpen ? "▾" : "▸"}</span>
            </button>
          ) : null}
          {tab === "review" ? (
            <a
              role="menuitem"
              className="ahead-cockpit-menu-item"
              href={glossaryHref()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
            >
              <span>Glossary</span>
              <span aria-hidden="true">↗</span>
            </a>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="ahead-cockpit-menu-item ahead-cockpit-menu-item-exit"
            onClick={() => {
              setOpen(false)
              onExit()
            }}
          >
            <span>Done</span>
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Right-docked slide-in cockpit. Owns the dock margin, collapse, the Edit/Review
 * tab switcher, and the shared cart. Edit-mode page overlays live in
 * EditModeHost (mounted while the Edit tab is active); the Review tab renders
 * ReviewWorklistBody.
 */
function DesktopCockpit({
  active,
  identity,
  onExit,
}: {
  active: boolean
  identity: { idToken: string; environment: string; branch?: string }
  onExit: () => void
}) {
  const [tab, setTab] = useState<Tab>(initialTab)
  const [collapsed, setCollapsed] = useState(false)
  const [preview, setPreview] = useState(true)
  const [reopen, setReopen] = useState<((item: CartItem) => void) | null>(null)
  // The Edit tab's body is just a host slot: EditModeHost portals either the
  // "hover any text" hint or the in-context string editor into it (AHD-7487),
  // so the on-page render stays visible on the left while you edit on the right.
  const [editSlot, setEditSlot] = useState<HTMLDivElement | null>(null)
  // The Review tab's scope / who-can-help / copy-link header is folded behind a
  // "Your scope" toggle by the tabs, so it doesn't dominate the worklist. (AHD-7494)
  const [scopeOpen, setScopeOpen] = useState(false)

  // Dock the page for the cockpit's whole lifetime; reflect collapse.
  useEffect(() => {
    if (!active) return
    const el = document.documentElement
    el.classList.add("ahead-cockpit-docked")
    return () => {
      el.classList.remove("ahead-cockpit-docked")
      el.classList.remove("ahead-cockpit-collapsed")
    }
  }, [active])
  useEffect(() => {
    document.documentElement.classList.toggle(
      "ahead-cockpit-collapsed",
      collapsed
    )
  }, [collapsed])
  // Remember the active tab so a cross-locale jump that remounts the cockpit
  // (see initialTab) comes back on Review instead of resetting to Edit.
  useEffect(() => {
    sessionStorage.setItem(COCKPIT_TAB_KEY, tab)
  }, [tab])

  if (!active) return null

  return (
    <div
      className={collapsed ? "ahead-cockpit is-collapsed" : "ahead-cockpit"}
      data-ahead-editor-ui
    >
      <div className="ahead-cockpit-head">
        <button
          className="ahead-cockpit-collapse"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? "Expand cockpit" : "Collapse cockpit"}
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? "‹" : "›"}
        </button>
        {!collapsed ? (
          <div className="ahead-cockpit-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === "edit"}
              className="ahead-cockpit-tab"
              onClick={() => setTab("edit")}
            >
              Edit
            </button>
            <button
              role="tab"
              aria-selected={tab === "review"}
              className="ahead-cockpit-tab"
              onClick={() => setTab("review")}
            >
              Review
            </button>
          </div>
        ) : null}
        {!collapsed ? (
          <CockpitHeadMenu
            tab={tab}
            scopeOpen={scopeOpen}
            onToggleScope={() => setScopeOpen((v) => !v)}
            onExit={onExit}
          />
        ) : null}
      </div>

      {collapsed ? null : (
        <div className="ahead-cockpit-body">
          {tab === "edit" ? (
            <div className="ahead-cockpit-edit-slot" ref={setEditSlot} />
          ) : (
            <ReviewWorklistBody
              idToken={identity.idToken}
              scopeOpen={scopeOpen}
              onScopeAutoOpen={() => setScopeOpen(true)}
            />
          )}
        </div>
      )}

      {/* Edit overlays must stay mounted whenever the Edit tab is active, even
          when collapsed is false but the body shows the hint. Mounted outside the
          collapsible body so page-level overlays/preview keep working. */}
      {tab === "edit" ? (
        <EditModeHost
          active={active}
          identity={identity}
          preview={preview}
          onTogglePreview={setPreview}
          onRegisterReopen={(fn) => setReopen(() => fn)}
          editSlot={editSlot}
        />
      ) : null}

      {collapsed ? null : (
        <CockpitCart
          identity={{ idToken: identity.idToken }}
          currentRef={identity.branch || "preflight"}
          env={identity.environment}
          preview={preview}
          onTogglePreview={setPreview}
          onEditItem={reopen ?? undefined}
        />
      )}
    </div>
  )
}

/**
 * Presentation fork for the cockpit: the draggable bottom sheet on mobile, the
 * right-docked rail on desktop. `useIsMobile` is false during SSR and in jsdom,
 * so server output and unit tests exercise DesktopCockpit.
 */
export function Cockpit(props: {
  active: boolean
  identity: { idToken: string; environment: string; branch?: string }
  onExit: () => void
}) {
  const isMobile = useIsMobile()
  if (isMobile) return <MobileCockpit {...props} />
  return <DesktopCockpit {...props} />
}
